import { MAX_HOUSEHOLD_RECIPES } from '@platewell/core';
import { useHouseholds, useIsOwner, usePlatewell, useRecipeQuota } from '@platewell/query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

/**
 * Dismissal is per person, per device, and expires with the tab.
 *
 * Never in the CRDT: a nudge one member waved away is not a fact about the household, and
 * syncing it would dismiss the banner on everyone else's phone too.
 */
function dismissKey(householdId: string): string {
  return `platewell.planNudgeDismissed.${householdId}`;
}

function readDismissed(householdId: string | null): boolean {
  if (householdId == null) return false;
  try {
    return window.sessionStorage.getItem(dismissKey(householdId)) === '1';
  } catch {
    return false;
  }
}

/**
 * The free tier's cap, explained in place.
 *
 * Three states: at the limit for an owner (who can fix it), at the limit for everyone else
 * (who cannot, and should be told whom to ask), and a dismissible nudge in the last couple
 * of slots. A household on an unlimited plan sees nothing until it reaches the ceiling.
 */
export function PlanBanner() {
  const quota = useRecipeQuota();
  const isOwner = useIsOwner();
  const { syncError } = usePlatewell();
  const { activeHouseholdId } = useHouseholds();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => readDismissed(activeHouseholdId));

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (activeHouseholdId == null) return;
    try {
      window.sessionStorage.setItem(dismissKey(activeHouseholdId), '1');
    } catch {
      // A browser with storage blocked just gets the banner again next reload.
    }
  }, [activeHouseholdId]);

  // The server measures the household after every update it applies, so on a device that
  // has just connected — and is still catching up — its count is ahead of the document's.
  // Nothing else consumed this payload before; the transport simply dropped it.
  const serverLimit = syncError?.code === 'quota_exceeded' ? syncError.limit : undefined;

  // Checked first: no plan lifts the ceiling, so this banner never offers to subscribe.
  if (quota.atCeiling || syncError?.code === 'recipe_ceiling') {
    return (
      <div className="banner warn" role="status">
        <span>
          This household has reached the {MAX_HOUSEHOLD_RECIPES.toLocaleString('en-US')}-recipe
          maximum. Delete some recipes to make room for new ones.
        </span>
      </div>
    );
  }

  if (quota.limit == null && serverLimit == null) return null;

  if (quota.atLimit || serverLimit != null) {
    return (
      <div className="banner warn" role="status">
        <span>
          You’ve reached the {quota.limit ?? serverLimit}-recipe free plan.{' '}
          {isOwner
            ? 'Subscribe to keep adding recipes — everyone in the household is covered.'
            : 'Ask an owner of this household to subscribe to add more.'}
        </span>
        {isOwner && (
          <button className="btn primary" onClick={() => void navigate({ to: '/subscribe' })}>
            Subscribe
          </button>
        )}
      </div>
    );
  }

  if (!quota.approaching || dismissed) return null;

  return (
    <div className="banner info" role="status">
      <span>
        {quota.remaining === 1
          ? 'One recipe slot left on the free plan.'
          : `${quota.remaining} recipe slots left on the free plan.`}
      </span>
      {isOwner && (
        <button className="btn" onClick={() => void navigate({ to: '/subscribe' })}>
          See plans
        </button>
      )}
      <button className="banner-dismiss" aria-label="Dismiss" onClick={dismiss}>
        ×
      </button>
    </div>
  );
}
